import { c as _c } from "react/compiler-runtime";
import { Badge, Button, CheckIcon, CloseButton, Indicator, Modal, Switch, TextInput, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { ProfileCfg, ProfileUI, zProfileCfg } from 'components/portal/profileTypes';
import { SAvatar, SAvatarGroup } from 'components/portal/SAvatar';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { doSafe } from 'shared/promise';
import { notNullOrEmpty } from 'shared/collection';
import { LabelForm, WrapIf } from 'shared/ui';
import { usePortal } from './portal';
import { ChangePasswordForm } from '../auth/ChangePasswordForm';
import { entries, groupBy, isEmpty, isNonNullish, keys, mapValues, pick, pipe, shuffle, sortBy, values } from 'remeda';
import { ComboMultiProps, ComboMulti, ItemMd } from 'components/form/ComboMulti';
import Image from 'next/image';
import { CardCollapsable } from 'components/Card';
import { P, match } from 'ts-pattern';
import { InfoTip } from 'components/Info';
import { HiArrowUturnLeft } from 'react-icons/hi2';
import { UseTour } from './Tour';
import { avatarNames } from './profile';
import { roleMd } from 'shared/forecast/roleTypes';
type ProfileForm = Pick<ProfileCfg, 'name' | 'notify' | 'avatar_url'>;
export const useAvatarSelection = () => {
  const $ = _c(6);
  const {
    profilesUI
  } = usePortal();
  let t0;
  if ($[0] !== profilesUI) {
    t0 = doSafe(profilesUI, _temp2);
    $[0] = profilesUI;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const profilesByAvatarUrl = t0;
  let t1;
  if ($[2] !== profilesByAvatarUrl) {
    t1 = profilesByAvatarUrl ? pipe(shuffle(entries(avatarNames).map(t2 => {
      const [key, names] = t2;
      const avatar_url = `/avatars/${key}.svg`;
      const existingProfiles = profilesByAvatarUrl[avatar_url] ?? [];
      return {
        type: "avatar" as const,
        key,
        avatar_url,
        name: shuffle(names).find(n => !existingProfiles.some(p_0 => p_0.name == n)) ?? names[0],
        avatarCount: existingProfiles.length
      };
    })), sortBy(_temp3)) : undefined;
    $[2] = profilesByAvatarUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const avatars = t1;
  let t2;
  if ($[4] !== avatars) {
    t2 = {
      avatars
    };
    $[4] = avatars;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

// split and capitalize common tokens
const nameFromEmail = (email: string) => email.split('@')[0]!.split(/[._+-]/).map(p => p[0]?.toUpperCase() + p.slice(1)).filter(notNullOrEmpty).join(' ');
export const ProfileForm = t0 => {
  const $ = _c(81);
  const {
    onSave,
    tour
  } = t0;
  const {
    profile,
    user,
    updateProfile,
    profilesUI
  } = usePortal();
  let t1;
  if ($[0] !== user?.app_metadata.providers) {
    t1 = user?.app_metadata.providers ?? [];
    $[0] = user?.app_metadata.providers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providers = t1 as string[];
  let t2;
  if ($[2] !== profile) {
    t2 = pick(profile ?? zProfileCfg.parse({}), ["name", "notify", "avatar_url"]);
    $[2] = profile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      name: _temp4
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = {
      initialValues: t2,
      validate: t3,
      validateInputOnBlur: true
    };
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const form = useForm(t4);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [avatarPopState, setAvatarPop] = useState();
  const {
    avatars
  } = useAvatarSelection();
  const {
    hideIdentities
  } = profile.workspace;
  const name = form.values.name;
  let t5;
  if ($[7] !== user) {
    t5 = user?.user_metadata.name ?? (user?.email ? nameFromEmail(user.email) : undefined);
    $[7] = user;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const defaultName = t5;
  let t6;
  if ($[9] !== defaultName || $[10] !== form || $[11] !== hideIdentities) {
    t6 = () => {
      if (form.values.name == "" && !hideIdentities && defaultName) {
        form.setValues({
          name: defaultName
        });
      }
    };
    $[9] = defaultName;
    $[10] = form;
    $[11] = hideIdentities;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(t6, t7);
  const avatarPop = avatarPopState ?? (form.values.name == "" && hideIdentities);
  let t8;
  if ($[14] !== form || $[15] !== onSave || $[16] !== updateProfile) {
    t8 = form.onSubmit(async v_0 => {
      form.resetDirty();
      await updateProfile(v_0);
      onSave?.();
    });
    $[14] = form;
    $[15] = onSave;
    $[16] = updateProfile;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== avatarPop || $[19] !== avatars || $[20] !== defaultName || $[21] !== form || $[22] !== hideIdentities || $[23] !== name || $[24] !== profilesUI || $[25] !== user?.user_metadata) {
    t9 = avatarPop ? <><div>{hideIdentities ? "Aliases are encouraged in this workspace. Only the admins can see your email" : "Real identities are encouraged in this workspace"}</div><div className="flex items-center"><CloseButton onClick={() => setAvatarPop(false)} /><div className="opacity-80">Select your profile</div></div>{profilesUI && avatars && (["special", "fresh"] as const).map(g => {
        const items = match(g).with("special", () => [{
          type: "special" as const,
          key: "initials" as const,
          name: isEmpty(name) ? defaultName : name
        }, doSafe(user?.user_metadata.avatar_url, u => ({
          type: "special" as const,
          key: "metadata" as const,
          avatar_url: u,
          name: user?.user_metadata.name
        }))].filter(isNonNullish)).with("fresh", _ => avatars).exhaustive();
        return !isEmpty(items) && <Fragment key={g}><div className="gap-2 w-full flex flex-wrap">{items.map(a => <button type="button" key={a.key} className="hover:bg-[var(--mantine-color-primary-light)] size-[8em] p-2 rounded-md" onClick={() => {
              match(a).with({
                avatar_url: P.string,
                name: P._
              }, t10 => {
                const {
                  avatar_url,
                  name: name_0
                } = t10;
                return form.setValues({
                  avatar_url,
                  ...(name_0 ? {
                    name: name_0
                  } : {})
                });
              }).with({
                key: "initials"
              }, t11 => {
                const {
                  name: name_1
                } = t11;
                return form.setValues({
                  avatar_url: undefined,
                  name: name_1
                });
              }).otherwise(_temp5);
              setAvatarPop(false);
            }}><div className="flex flex-col justify-center gap-2"><div className="self-center">{match(a).with({
                    type: "avatar"
                  }, _temp6).with({
                    key: "initials"
                  }, _temp7).with({
                    key: "metadata"
                  }, _temp8).exhaustive()}</div>{a.name && <div className="opacity-60 font-semibold text-sm">{a.name}</div>}</div></button>)}</div></Fragment>;
      })}</> : <div className="flex gap-2 items-center"><button className="hover:bg-[var(--mantine-color-default-hover)] p-4" onClick={e => {
        e.preventDefault();
        setAvatarPop(true);
      }}><SAvatar size="lg" profile={form.values} /></button><TextInput label="Name" {...form.getInputProps("name")} /></div>;
    $[18] = avatarPop;
    $[19] = avatars;
    $[20] = defaultName;
    $[21] = form;
    $[22] = hideIdentities;
    $[23] = name;
    $[24] = profilesUI;
    $[25] = user?.user_metadata;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LabelForm>roles:</LabelForm>;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== profile.roles) {
    t11 = profile?.roles?.map(_temp9).map(_temp10);
    $[28] = profile.roles;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <div className="flex flex-row items-center gap-2">{t10}{t11}</div>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== t9) {
    t13 = <div>{t9}{t12}</div>;
    $[32] = t12;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      container: "space-y-2"
    };
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== form) {
    t15 = form.getInputProps("notify.email", {
      type: "checkbox"
    });
    $[36] = form;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15) {
    t16 = <Switch label="Send notifications to" {...t15} />;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = user?.email ?? "";
  let t18;
  if ($[40] !== t17) {
    t18 = <TextInput disabled={true} value={t17} />;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <InfoTip label="Email is used to send notifications, and for authentication but is not visible to others" />;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t16 || $[44] !== t18) {
    t20 = <div className="flex gap-2 pt-2 items-center">{t16}{t18}{t19}</div>;
    $[43] = t16;
    $[44] = t18;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== tour.cfg || $[47] !== tour.settings.noTips || $[48] !== tour.settings.seen) {
    t21 = tour.settings.noTips ? "All intro tips hidden" : <>Seen <b>{tour.settings.seen.length}</b> / {keys(tour.cfg).length} intro tips</>;
    $[46] = tour.cfg;
    $[47] = tour.settings.noTips;
    $[48] = tour.settings.seen;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t21) {
    t22 = <div>{t21}.</div>;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <HiArrowUturnLeft />;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== tour) {
    t24 = <Button leftSection={t23} onClick={() => {
      tour.reset();
    }}>Reset tips</Button>;
    $[53] = tour;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t22 || $[56] !== t24) {
    t25 = <div className="flex items-center">{t22}{t24}</div>;
    $[55] = t22;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t20 || $[59] !== t25) {
    t26 = <CardCollapsable title="Advanced" startCollapsed={true} className="min-w-[20em]" classNames={t14}>{t20}{t25}</CardCollapsable>;
    $[58] = t20;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== form) {
    t27 = !form.isDirty() || !form.isValid();
    $[61] = form;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t27) {
    t28 = <Button variant="filled" type="submit" disabled={t27}>Save</Button>;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== providers) {
    t29 = providers.includes("email") && <Button onClick={() => setShowChangePassword(true)}>Change password</Button>;
    $[65] = providers;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  let t31;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => setShowChangePassword(false);
    t31 = <h2>Update password</h2>;
    $[67] = t30;
    $[68] = t31;
  } else {
    t30 = $[67];
    t31 = $[68];
  }
  let t32;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <ChangePasswordForm close={() => setShowChangePassword(false)} buttonLabel="Update password" />;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== showChangePassword) {
    t33 = <Modal opened={showChangePassword} onClose={t30} title={t31}>{t32}</Modal>;
    $[70] = showChangePassword;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t28 || $[73] !== t29 || $[74] !== t33) {
    t34 = <div className="mt-4 flex gap-6">{t28}{t29}{t33}</div>;
    $[72] = t28;
    $[73] = t29;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t13 || $[77] !== t26 || $[78] !== t34 || $[79] !== t8) {
    t35 = <form className="flex flex-col gap-8 items-start w-full" onSubmit={t8}>{t13}{t26}{t34}</form>;
    $[76] = t13;
    $[77] = t26;
    $[78] = t34;
    $[79] = t8;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  return t35;
};
export const ProfileFilter = t0 => {
  const $ = _c(19);
  let counts;
  let emptyLabel;
  let emptyTarget;
  let props;
  if ($[0] !== t0) {
    ({
      counts,
      emptyTarget,
      emptyLabel,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = counts;
    $[2] = emptyLabel;
    $[3] = emptyTarget;
    $[4] = props;
  } else {
    counts = $[1];
    emptyLabel = $[2];
    emptyTarget = $[3];
    props = $[4];
  }
  const {
    profilesUI
  } = usePortal();
  let t1;
  if ($[5] !== profilesUI) {
    t1 = profilesUI ?? {};
    $[5] = profilesUI;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== counts) {
    t2 = (v, k) => ({
      count: counts?.[k],
      ...v
    });
    $[7] = counts;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = mapValues(t1, t2);
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== emptyLabel || $[13] !== emptyTarget) {
    t4 = emptyTarget ?? <div className="text-gray-400">{emptyLabel ?? "all participants"}</div>;
    $[12] = emptyLabel;
    $[13] = emptyTarget;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== props || $[16] !== t3 || $[17] !== t4) {
    t5 = <ComboMulti md={t3} item={_temp11} emptyTarget={t4} target={_temp13} {...props} />;
    $[15] = props;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
export const ProfileFilterItem = t0 => {
  const $ = _c(19);
  let count;
  let profile;
  let selected;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      selected,
      count,
      ...profile
    } = t0);
    $[0] = t0;
    $[1] = count;
    $[2] = profile;
    $[3] = selected;
    $[4] = value;
  } else {
    count = $[1];
    profile = $[2];
    selected = $[3];
    value = $[4];
  }
  let t1;
  if ($[5] !== selected) {
    t1 = selected && <CheckIcon size="1em" className="text-gray-400" />;
    $[5] = selected;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== profile || $[8] !== value) {
    t2 = <SAvatar size="sm" key={value} profile={profile} />;
    $[7] = profile;
    $[8] = value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== profile.name) {
    t3 = <div className="font-semibold">{profile.name}</div>;
    $[10] = profile.name;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== count) {
    t4 = <div className="ml-auto font-numbers text-gray-400 font-semibold pl-2">{count}</div>;
    $[12] = count;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <div className="flex gap-2 items-center justify-center">{t1}{t2}{t3}{t4}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};

// const avatarMds = mapEntries(avatarNames, (key, names) =>
//   names.map(name => ({name, avatar_url: `/avatars/${key}.svg`}))
// ).flat()
function _temp(p) {
  return p.avatar_url;
}
function _temp2(ps) {
  return groupBy(values(ps), _temp);
}
function _temp3(p_1) {
  return p_1.avatarCount;
}
function _temp4(v) {
  return v.length < 2 ? "name too short" : undefined;
}
function _temp5() {}
function _temp6(t0) {
  const {
    avatar_url: avatar_url_0,
    name: name_2,
    key,
    avatarCount
  } = t0;
  return <WrapIf if={avatarCount > 0} wrap={c => <Indicator classNames={{
    indicator: "!h-[1em] font-bold"
  }} label={`${avatarCount} use`}>{c}</Indicator>}><Image src={avatar_url_0} alt={name_2 ?? key} width={38} height={38} /></WrapIf>;
}
function _temp7(t0) {
  const {
    name: name_3
  } = t0;
  return <div className="opacity-60 font-semibold text-sm"><SAvatar size="lg" profile={{
      name: name_3
    }} /></div>;
}
function _temp8(p) {
  return <SAvatar size="lg" profile={p} />;
}
function _temp9(r) {
  return {
    name: r,
    ...roleMd[r]
  };
}
function _temp10(t0) {
  const {
    name: name_4,
    label,
    desc
  } = t0;
  return <div key={name_4}><Tooltip label={<div className="opacity-80">You have the <b>{label}</b> role. {desc}</div>}><Badge>{name_4}</Badge></Tooltip></div>;
}
function _temp11(p) {
  return <ProfileFilterItem {...p} />;
}
function _temp12(i) {
  return i.selected;
}
function _temp13(items) {
  return <SAvatarGroup profiles={items.filter(_temp12)} />;
}